import React, { useCallback, useState } from 'react';

import type { UUID } from '@aline/common';
import { UserRound } from 'lucide-react';
import { useCanAssignWorkflowRoles } from 'src/components/workflow/assign-roles';
import { AssignRolesDialog } from 'src/components/workflow/AssignRolesDialog';
import {
  WorkflowBannerAction,
  WorkflowBannerCard,
} from 'src/components/workflow/workflow-detail/WorkflowBannerCard';
import { useGetUnassignedWorkflowPermissions } from 'src/hooks/assignment';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  roleChip: {
    backgroundColor: theme.appColors.background,
    border: `1px solid ${theme.appColors.ruleSubtle}`,
    borderRadius: 6,
    color: theme.palette.text.primary,
    fontSize: 12,
    lineHeight: '18px',
    padding: '0 6px',
    whiteSpace: 'nowrap',
  },
  roles: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
  },
}));

/** Names the workflow's roles nobody fills yet, with one action to fill them all. */
export const UnassignedRolesAlert = React.memo(function UnassignedRolesAlert({
  workflowId,
}: {
  workflowId: UUID;
}): JSX.Element | null {
  const canAssign = useCanAssignWorkflowRoles(workflowId);
  if (!canAssign) return null;
  return <UnassignedRolesAlertContent workflowId={workflowId} />;
});

function UnassignedRolesAlertContent({ workflowId }: { workflowId: UUID }): JSX.Element | null {
  const { classes } = useStyles();
  const [open, setOpen] = useState(false);
  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);
  const { unassignedWorkflowPermissions } = useGetUnassignedWorkflowPermissions({ workflowId });
  const roleNames = unassignedWorkflowPermissions.flatMap(({ role }) =>
    role ? [role.roleName] : []
  );
  if (!roleNames.length) return null;

  return (
    <>
      <WorkflowBannerCard
        action={
          <WorkflowBannerAction onClick={handleOpen}>
            {roleNames.length > 1 ? 'Assign roles' : 'Assign'}
          </WorkflowBannerAction>
        }
        dataCy="unassigned-roles-alert"
        heading={getUnassignedRolesTitle(roleNames.length)}
        icon={UserRound}
        severity="warning"
        subtitle={
          <div className={classes.roles}>
            {roleNames.map(roleName => (
              <span key={roleName} className={classes.roleChip}>
                {roleName}
              </span>
            ))}
          </div>
        }
      />
      <AssignRolesDialog
        open={open}
        title="Assign roles"
        workflowId={workflowId}
        onClose={handleClose}
      />
    </>
  );
}

function getUnassignedRolesTitle(roleCount: number): string {
  return roleCount === 1 ? 'A role needs to be assigned' : `${roleCount} roles need to be assigned`;
}
